/* global window */

import videojs from 'video.js';
import adblockDetect from 'adblock-detect';
import canAutoPlay from 'can-autoplay';
import get from 'lodash/get';

import settings from './settings';
import EncoreInjector from './EncoreInjector';
import {
  catchErrors,
  whenDomReady,
  loadScript,
  checkWebPFeature,
} from './utils';

/**
 * Check if any sort of ad blocking extension is enabled.
 * @returns {Promise<boolean>} ad block detected.
 */
function detectAdBlock() {
  return new Promise(resolve => {
    adblockDetect(adBlockDetected => {
      return resolve(adBlockDetected);
    });
  });
}

/**
 * Check if video autoplay is allowed.
 * @returns {Promise<boolean>} autoplay allowed.
 */
function detectAutoplay() {
  return new Promise(resolve => {
    canAutoPlay
      .video({
        timeout: 250,
        inline: true,
      })
      .then(({ result, error }) => {
        if (result === false) {
          videojs.log(`canAutoplay error: [${error}]`);
        }
        return resolve(result);
      });
  });
}

/**
 * Detect WebP support.
 * @returns {Promise<boolean>} WebP capable.
 */
function detectWebP() {
  return new Promise(resolve => {
    checkWebPFeature('animation', (feature, result) => {
      if (feature === 'animation') {
        return resolve(result);
      }
      return resolve(false);
    });
  });
}

/**
 * Kickstart video player. Insert player variable into global context.
 */
function kickstart() {
  window.hearstPlayer = new EncoreInjector(settings);
  window.hearstPlayer.initialize(window);
}

/**
 * Perform all pre-player initialization tasks.
 * Once finished, kickstart video player.
 * @async
 */
async function bootstrap() {
  const [
    domReady,
    imaSdkLoaded,
    canAutoplay,
    adBlockDetected,
    canUseWebP,
  ] = await Promise.all([
    catchErrors(whenDomReady()),
    catchErrors(loadScript(settings.imaSdkUrl)),
    catchErrors(detectAutoplay()),
    catchErrors(detectAdBlock()),
    catchErrors(detectWebP()),
  ]);

  const imaSdkReady = typeof get(window, 'google.ima') !== 'undefined';
  const imaSdkAvailable = imaSdkLoaded && imaSdkReady;
  settings.imaSdkAvailable = imaSdkAvailable;
  settings.canAutoplay = canAutoplay;
  settings.adBlockDetected = adBlockDetected;
  settings.canUseWebP = canUseWebP;

  videojs.log(`domReady = [${domReady}]`);
  videojs.log(`imaSdkAvailable = [${imaSdkAvailable}]`);
  videojs.log(`canAutoplay = [${canAutoplay}]`);
  videojs.log(`adBlockDetected = [${adBlockDetected}]`);
  videojs.log(`canUseWebP = [${canUseWebP}]`);

  kickstart();
}

export default bootstrap;
